<script setup lang="ts">
/**
 * A confirm dialog: title, message, and the answer as buttons — confirm and
 * cancel, optionally a third action at the start of the row (Save / Don't save
 * / Cancel). `tone="danger"` marks a destructive step: a warning badge next to
 * the message, a red confirm button, and the focus starts on cancel so an
 * Enter does not destroy anything by accident.
 *
 * Presentational: `v-model:open` plus `confirm` / `alternative` / `cancel`
 * events. Closing by the ✕, Escape or the backdrop counts as `cancel`. For the
 * awaitable form that replaces `window.confirm()`, use `useNxConfirm()` with
 * an `NxConfirmHost` in the shell.
 */
import TriangleAlert from 'lucide-vue-next/dist/esm/icons/triangle-alert.js';
import { nextTick, useId, useTemplateRef, watch } from 'vue';
import { NxButton } from '../Button';
import { NxModal } from '../Modal';
import type { NxConfirmTone } from './types';

const props = withDefaults(
  defineProps<{
    title: string;
    /** Body text, shown as written (line breaks kept). */
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Third action at the start of the button row; emits `alternative`. */
    alternativeLabel?: string;
    tone?: NxConfirmTone;
    /** The confirmed step runs: buttons disabled, dialog stays open. */
    busy?: boolean;
  }>(),
  {
    message: undefined,
    confirmLabel: 'Bestätigen',
    cancelLabel: 'Abbrechen',
    alternativeLabel: undefined,
    tone: 'default',
    busy: false,
  }
);

const open = defineModel<boolean>('open', { required: true });

const emit = defineEmits<{
  confirm: [];
  alternative: [];
  cancel: [];
}>();

defineSlots<{
  /** Replaces the message — for rich content (a list of affected items). */
  default?: () => unknown;
}>();

const messageId = useId();
const bodyRef = useTemplateRef<HTMLElement>('body');
const confirmRef = useTemplateRef<InstanceType<typeof NxButton>>('confirm');
const cancelRef = useTemplateRef<InstanceType<typeof NxButton>>('cancel');

// Set when a button answered, so the modal's own close (✕, Escape, backdrop)
// is not reported a second time as `cancel`.
let answered = false;

watch(
  open,
  async (isOpen) => {
    if (!isOpen) return;
    answered = false;
    // Two ticks: the shell renders the <dialog>, then showModal() moves focus.
    await nextTick();
    await nextTick();
    // The <dialog> belongs to NxModal: describe it by the message from here.
    bodyRef.value?.closest('dialog')?.setAttribute('aria-describedby', messageId);
    const target = props.tone === 'danger' ? cancelRef.value : confirmRef.value;
    target?.focus({ focusVisible: true } as FocusOptions);
  },
  { immediate: true }
);

function answer(choice: 'confirm' | 'alternative' | 'cancel') {
  if (props.busy) return;
  answered = true;
  if (choice === 'confirm') emit('confirm');
  else if (choice === 'alternative') emit('alternative');
  else emit('cancel');
  open.value = false;
}

function onShellClose() {
  if (answered) return;
  answered = true;
  emit('cancel');
}
</script>

<template>
  <NxModal
    v-model:open="open"
    class="nx-confirm-dialog"
    :title="props.title"
    :close-on-escape="!props.busy"
    :close-on-click-outside="!props.busy"
    size="default"
    :resizable="false"
    @close="onShellClose"
  >
    <template #body>
      <div ref="body" class="nx-confirm-dialog__body" :class="`tone-${props.tone}`">
        <span v-if="props.tone === 'danger'" class="nx-confirm-dialog__badge" aria-hidden="true">
          <TriangleAlert class="nxt-icon-md" />
        </span>
        <div :id="messageId" class="nx-confirm-dialog__message">
          <slot>{{ props.message }}</slot>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="nx-confirm-dialog__actions" :aria-busy="props.busy || undefined">
        <NxButton
          v-if="props.alternativeLabel"
          class="nx-confirm-dialog__alternative"
          variant="ghost"
          :disabled="props.busy"
          data-testid="nx-confirm-alternative"
          @click="answer('alternative')"
        >
          {{ props.alternativeLabel }}
        </NxButton>
        <NxButton
          ref="cancel"
          variant="outline"
          :disabled="props.busy"
          data-testid="nx-confirm-cancel"
          @click="answer('cancel')"
        >
          {{ props.cancelLabel }}
        </NxButton>
        <NxButton
          ref="confirm"
          :variant="props.tone === 'danger' ? 'danger' : 'default'"
          :disabled="props.busy"
          data-testid="nx-confirm-confirm"
          @click="answer('confirm')"
        >
          <span v-if="props.busy" class="nx-confirm-dialog__spinner" aria-hidden="true" />
          {{ props.confirmLabel }}
        </NxButton>
      </div>
    </template>
  </NxModal>
</template>

<style scoped>
.nx-confirm-dialog__body {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-md);
  padding-block: var(--nxt-space-2xs) var(--nxt-space-sm);
}
.nx-confirm-dialog__badge {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-control-size-lg);
  height: var(--nxt-control-size-lg);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-danger-subtle-bg);
  color: var(--nxt-danger-subtle-fg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 4)
    color-mix(in srgb, var(--nxt-danger-subtle-bg) 45%, transparent);
  animation: nx-confirm-badge-in var(--nxt-motion-base) var(--nxt-ease-out);
}
.nx-confirm-dialog__message {
  flex: 1 1 auto;
  min-width: 0;
  padding-block-start: var(--nxt-space-2xs);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.nx-confirm-dialog__body.tone-danger .nx-confirm-dialog__message {
  padding-block-start: var(--nxt-space-xs);
}
.nx-confirm-dialog__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--nxt-space-xs);
  width: 100%;
}
.nx-confirm-dialog__alternative {
  margin-inline-end: auto;
}
.nx-confirm-dialog__spinner {
  display: inline-block;
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  margin-inline-end: var(--nxt-space-2xs);
  border: calc(var(--nxt-hairline) * 2) solid currentcolor;
  border-inline-end-color: transparent;
  border-radius: var(--nxt-radius-full);
  vertical-align: middle;
  animation: nx-confirm-spin 0.8s linear infinite;
}
@keyframes nx-confirm-badge-in {
  from {
    transform: scale(0.6);
    opacity: 0;
  }
}
@keyframes nx-confirm-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-confirm-dialog__badge {
    animation: none;
  }
  .nx-confirm-dialog__spinner {
    animation-duration: 2.4s;
  }
}
</style>
